import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bike, Phone, Plus, Trash2, UserRound } from "lucide-react";
import { adminApi, formatApiError } from "../lib/api";

const CHIP = {
  free: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  busy: "border-carza/40 bg-carza-dim text-carza-lite",
  offline: "border-white/10 bg-white/[0.03] text-mist-2",
};

export default function AdminPartners({ token }) {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", bike: "", pin: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      setPartners(await adminApi.partners(token));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.addPartner(form, token);
      toast.success(`${form.name.split(" ")[0]} added to the team`);
      setForm({ name: "", phone: "", bike: "", pin: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setAvailability = async (p, availability) => {
    try {
      await adminApi.updatePartner(p.partner_id, { availability }, token);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the team?`)) return;
    try {
      await adminApi.deletePartner(p.partner_id, token);
      toast.success("Partner removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={submit} className="card-surface grid grid-cols-2 gap-3 rounded-2xl p-5 sm:grid-cols-6" data-testid="partner-add-form">
        <div className="col-span-2 sm:col-span-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Add partner</p>
          <p className="mt-1 text-[11px] leading-relaxed text-mist">Field rider who washes at doorsteps.</p>
        </div>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">Name</span>
          <input className="field" value={form.name} onChange={set("name")} required placeholder="Rajesh Kumar" data-testid="partner-name-input" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">Phone</span>
          <input className="field" value={form.phone} onChange={set("phone")} required placeholder="10-digit mobile" inputMode="tel" data-testid="partner-phone-input" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">Bike / Vehicle</span>
          <input className="field" value={form.bike} onChange={set("bike")} placeholder="Activa · UP16 AB 1234" data-testid="partner-bike-input" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">Login PIN</span>
          <input className="field" value={form.pin} onChange={set("pin")} required placeholder="4-6 digits" inputMode="numeric" data-testid="partner-pin-input" />
        </label>
        <button type="submit" disabled={saving} className="btn btn-primary self-end px-4 py-2.5 text-[13px] disabled:opacity-50" data-testid="partner-add-submit">
          {saving ? "Adding…" : <>Add <Plus size={14} /></>}
        </button>
      </form>

      {!loaded ? (
        <div className="flex h-32 items-center justify-center text-sm text-mist-2">Loading partners…</div>
      ) : partners.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-sm text-mist-2" data-testid="partners-empty">
          No partners yet. Add your first rider above — then assign paid bookings to them from the Bookings tab.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {partners.map((p) => (
            <div key={p.partner_id} className="card-surface rounded-2xl p-5" data-testid={`partner-card-${p.partner_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-carza-dim text-carza-lite"><UserRound size={18} /></span>
                  <div>
                    <p className="text-[15px] font-semibold text-paper">{p.name}</p>
                    <p className="flex items-center gap-1.5 text-[12px] text-mist"><Phone size={11} /> +91 {p.phone}</p>
                    {p.bike && <p className="flex items-center gap-1.5 text-[12px] text-mist"><Bike size={11} /> {p.bike}</p>}
                  </div>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest ${CHIP[p.availability] || CHIP.offline}`} data-testid={`partner-status-${p.partner_id}`}>
                  {p.availability}
                </span>
              </div>
              {p.current_job ? (
                <div className="mt-3 rounded-xl border border-carza/25 bg-carza/[0.06] p-3 text-[12px] leading-relaxed text-mist" data-testid={`partner-job-${p.partner_id}`}>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-carza-lite">On job — {p.current_job.booking_id}</span>
                  <p className="mt-1 text-paper">{p.current_job.customer_name} · {p.current_job.plan_name}</p>
                  <p>{p.current_job.address}</p>
                  <p>{p.current_job.date} · {p.current_job.time_slot}</p>
                </div>
              ) : (
                <p className="mt-3 text-[12px] text-mist-2">No active job.</p>
              )}
              <div className="mt-4 flex items-center justify-between gap-2">
                <div className="flex gap-2">
                  {p.availability !== "free" && (
                    <button onClick={() => setAvailability(p, "free")} className="btn btn-outline px-3 py-1.5 text-[11px]" data-testid={`partner-free-${p.partner_id}`}>Mark Free</button>
                  )}
                  {p.availability !== "offline" && (
                    <button onClick={() => setAvailability(p, "offline")} className="btn btn-ghost px-3 py-1.5 text-[11px]" data-testid={`partner-off-${p.partner_id}`}>Off Duty</button>
                  )}
                </div>
                <button onClick={() => remove(p)} className="flex items-center gap-1 text-[11px] text-mist-2 transition-colors hover:text-red-300" data-testid={`partner-delete-${p.partner_id}`}>
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
